/**
 * Direitos da conta no painel (matriz de funcionalidades, F3; plano, seção 6.1).
 *
 * O `/me` traz o valor efetivo de cada chave (`entitlements.values`), já com o estado da conta
 * aplicado pelo servidor. As telas perguntam por aqui, no lugar das regras soltas:
 * - `can`: liga/desliga;
 * - `level` e `atLeast`: níveis (`off` < `count_only` < `on`…);
 * - `has`: item de um conjunto;
 * - `limit`: limite (`null` = sem limite);
 * - `hidesWhenOff`: desligado some ou aparece travado (decisão 273).
 *
 * Valem as regras antigas (`licensePolicy.ts`, `tagLimit`, `demo`, `modules`) quando:
 * - o `/me` não traz `entitlements` (servidor antigo), o que deixa publicar o painel antes;
 * - a licença venceu depois do `/me`: as ações pagas falham fechadas, como antes.
 */
import type { Entitlements, EntitlementValue, Session } from '../types/session';
import { TAG_LIMIT_DEFAULT } from '../config/constants';
import { canStartCapture, canUseLeadExplorer, canUsePaidFeature } from './licensePolicy';

export type ToggleKey = 'capture.enabled' | 'whatsapp.link' | 'ai.chat_enabled';

export type LevelKey =
  'explorer.mode' | 'lookalike.contacts' | 'lookalike.places' | 'enrich.places' | 'capture.speed';

export type SetKey = 'search.types' | 'export.formats' | 'content.items' | 'explorer.addons';

export type LimitKey =
  | 'capture.results_max'
  | 'capture.lists_max'
  | 'lists.readable_max'
  | 'search.places.terms_max'
  | 'search.phones.terms_max'
  | 'search.emails.terms_max'
  | 'search.phones.sources_max'
  | 'search.emails.sources_max'
  | 'search.emails.domains_max'
  | 'maps.locations_max'
  | 'maps.radius_km_max'
  | 'lookalike.places_queries_max';

export type EntitlementKey = ToggleKey | LevelKey | SetKey | LimitKey;

/** Ordem de cada nível, do mais fechado ao mais aberto (catálogo do servidor). */
const LEVELS: Record<LevelKey, readonly string[]> = {
  'explorer.mode': ['off', 'masked', 'on'],
  'lookalike.contacts': ['off', 'count_only', 'on'],
  'lookalike.places': ['off', 'count_only', 'on'],
  'enrich.places': ['off', 'count_only', 'on'],
  'capture.speed': ['normal', 'fast'],
};

/** Chaves que somem quando desligadas, se o `/me` não mandar a lista (catálogo, `whenOff`). */
const HIDE_WHEN_OFF = [
  'account.seats',
  'account.workspaces_max',
  'account.referral',
  'backup.months',
  'content.items',
];

/** Os números que as telas usavam fixos antes dos direitos. */
export const LEGACY_PLACES_TERMS = 3;
export const LEGACY_RADIUS_KM = 16;
export const LEGACY_DEMO_VISIBLE = 5;
export const LEGACY_SIMILAR_CALLS = 400;

const SEARCH_TYPES = ['places', 'phones', 'emails'];
const EXPORT_FORMATS = ['xlsx', 'csv', 'txt', 'json'];
const CONTENT_ITEMS = ['leadtalk', 'leadmusic'];
const EXPLORER_ADDONS = [
  'technologies',
  'traffic',
  'site_contacts',
  'ai_research',
  'ai_prospecting_plan',
  'registries_fr_uk',
];

export type EntitlementSession =
  | (Pick<Session, 'status' | 'expiredAt' | 'demo' | 'modules'> &
      Partial<Pick<Session, 'tagLimit' | 'entitlements'>>)
  | null
  | undefined;

type Values = Record<EntitlementKey, EntitlementValue>;

function isFutureDate(value: unknown, now: number): boolean {
  if (typeof value !== 'string' || value.trim().length === 0) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > now;
}

/** O que as regras antigas davam a cada chave; o mesmo comportamento de antes da F3. */
function legacyValues(session: EntitlementSession, now: number): Values {
  const paid = canUsePaidFeature(session, now);
  const capture = canStartCapture(session, now);
  const modules = session?.modules ?? {};
  const tagLimit = session?.tagLimit ?? TAG_LIMIT_DEFAULT;
  return {
    'capture.enabled': capture,
    'whatsapp.link': true,
    'ai.chat_enabled': isFutureDate(modules['leadchat'], now),
    // Sem o módulo, o dossiê sai embaralhado; sem licença paga, o LeadExplorer não abre.
    'explorer.mode': canUseLeadExplorer(session, now) ? 'on' : paid ? 'masked' : 'off',
    // Com o LeadExplorer completo, a IA vinha junto (hoje, o dossiê com IA).
    'explorer.addons': canUseLeadExplorer(session, now) ? EXPLORER_ADDONS : [],
    // Sem licença paga, a janela de semelhantes abre no convite, com a contagem.
    'lookalike.contacts': paid ? 'on' : 'count_only',
    // Semelhantes de negócios locais seguiam a captura (decisão 186).
    'lookalike.places': capture ? 'on' : 'off',
    'enrich.places': paid ? 'on' : 'off',
    // Sem direitos no `/me`, a velocidade de sempre.
    'capture.speed': 'fast',
    'search.types': SEARCH_TYPES.filter((type) => Boolean(modules[type])),
    'export.formats': EXPORT_FORMATS,
    'content.items': CONTENT_ITEMS.filter((item) => modules[item] === true),
    'lists.readable_max': session?.demo === true ? LEGACY_DEMO_VISIBLE : null,
    'capture.results_max': null,
    'capture.lists_max': null,
    'search.places.terms_max': LEGACY_PLACES_TERMS,
    'search.phones.terms_max': null,
    'search.emails.terms_max': null,
    'search.phones.sources_max': tagLimit,
    'search.emails.sources_max': tagLimit,
    'search.emails.domains_max': tagLimit,
    'maps.locations_max': tagLimit,
    'maps.radius_km_max': LEGACY_RADIUS_KM,
    'lookalike.places_queries_max': LEGACY_SIMILAR_CALLS,
  };
}

/** Os direitos do `/me` valem para esta conta agora? */
export function usesEntitlements(session: EntitlementSession, now = Date.now()): boolean {
  const entitlements = session?.entitlements;
  if (!entitlements || !entitlements.values) return false;
  // Venceu depois do `/me` (licença ou teste): as regras antigas já olham a data a cada pergunta.
  if (
    (entitlements.state === 'active' || entitlements.state === 'trial') &&
    !isFutureDate(entitlements.license?.expiresAt, now)
  ) {
    return false;
  }
  return true;
}

function valueOf(
  session: EntitlementSession,
  key: EntitlementKey,
  now: number,
): EntitlementValue | undefined {
  if (usesEntitlements(session, now)) return session!.entitlements!.values[key];
  return legacyValues(session, now)[key];
}

/** Liga/desliga. Valor ausente ou de outro tipo conta como desligado. */
export function can(session: EntitlementSession, key: ToggleKey, now = Date.now()): boolean {
  return valueOf(session, key, now) === true;
}

/** Nível da chave; ausente ou desconhecido vira o mais fechado. */
export function level(session: EntitlementSession, key: LevelKey, now = Date.now()): string {
  const value = valueOf(session, key, now);
  return typeof value === 'string' && LEVELS[key].includes(value)
    ? value
    : (LEVELS[key][0] ?? 'off');
}

/** "Pelo menos `minimum`" num nível. */
export function atLeast(
  session: EntitlementSession,
  key: LevelKey,
  minimum: string,
  now = Date.now(),
): boolean {
  const order = LEVELS[key];
  const min = order.indexOf(minimum);
  return min >= 0 && order.indexOf(level(session, key, now)) >= min;
}

/** No modo "Normal", cada espera dobra: metade da velocidade de hoje (decisão 299). */
export const NORMAL_SPEED_FACTOR = 2;

/**
 * Quantas vezes mais devagar a captura, o enriquecimento e os semelhantes andam: 1 no "Rápido"
 * (a velocidade de hoje), 2 no "Normal". O LeadExplorer não usa.
 */
export function speedFactor(session: EntitlementSession, now = Date.now()): number {
  return level(session, 'capture.speed', now) === 'fast' ? 1 : NORMAL_SPEED_FACTOR;
}

/** O item está no conjunto da chave. */
export function has(
  session: EntitlementSession,
  key: SetKey,
  item: string,
  now = Date.now(),
): boolean {
  const value = valueOf(session, key, now);
  return Array.isArray(value) && value.includes(item);
}

/** Os itens do conjunto, na ordem do servidor. */
export function itemsOf(session: EntitlementSession, key: SetKey, now = Date.now()): string[] {
  const value = valueOf(session, key, now);
  return Array.isArray(value) ? value.slice() : [];
}

/** Limite da chave: `null` = sem limite; ausente ou inválido = 0 (falha fechada). */
export function limit(session: EntitlementSession, key: LimitKey, now = Date.now()): number | null {
  const value = valueOf(session, key, now);
  if (value === null) return null;
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

const TERMS_KEY: Record<string, LimitKey> = {
  places: 'search.places.terms_max',
  phones: 'search.phones.terms_max',
  emails: 'search.emails.terms_max',
};

const SOURCES_KEY: Record<string, LimitKey> = {
  phones: 'search.phones.sources_max',
  emails: 'search.emails.sources_max',
};

/**
 * Termos de uma busca, como o servidor conta (`guard.termsOf`): cada parte separada por vírgula ou
 * ponto e vírgula vale um termo. Em Negócios locais, cada parte vira uma busca no Maps.
 */
export function countTerms(keywords: readonly string[]): number {
  let total = 0;
  for (const group of keywords) {
    for (const part of String(group).split(/[,;|]/)) {
      if (part.trim().length > 1) total += 1;
    }
  }
  return total;
}

/** Limites de uma busca do tipo (decisões 262 e 270). `null` = sem limite. */
export interface SearchLimits {
  terms: number | null;
  sources: number | null;
  domains: number | null;
  locations: number | null;
  radiusKm: number | null;
}

export function searchLimits(
  session: EntitlementSession,
  module: string,
  now = Date.now(),
): SearchLimits {
  const terms = TERMS_KEY[module];
  const sources = SOURCES_KEY[module];
  return {
    terms: terms ? limit(session, terms, now) : null,
    sources: sources ? limit(session, sources, now) : null,
    domains: limit(session, 'search.emails.domains_max', now),
    locations: limit(session, 'maps.locations_max', now),
    radiusKm: limit(session, 'maps.radius_km_max', now),
  };
}

/**
 * Quantas listas novas do tipo a conta ainda pode criar (teste: 2 por tipo; decisão 290). `null` =
 * sem limite. O uso vem do `/me` (o servidor conta inclusive as apagadas).
 */
export function listsLeft(
  session: EntitlementSession,
  module: string,
  now = Date.now(),
): number | null {
  // Servidor anterior à F7 não manda a chave: sem limite, e não "acabaram" para todo mundo.
  if (usesEntitlements(session, now) && !('capture.lists_max' in session!.entitlements!.values))
    return null;
  const max = limit(session, 'capture.lists_max', now);
  if (max === null) return null;
  const type = module === 'email' ? 'emails' : module;
  if (type !== 'emails' && type !== 'phones' && type !== 'places') return null;
  const used = session?.entitlements?.lists?.[type] ?? 0;
  return Math.max(0, max - used);
}

/** Desligada, a chave some da tela (`hide`) em vez de aparecer travada com a oferta (273). */
export function hidesWhenOff(session: EntitlementSession, key: string): boolean {
  const hide = session?.entitlements?.hide;
  return (Array.isArray(hide) ? hide : HIDE_WHEN_OFF).includes(key);
}

/**
 * Tipos de busca que aparecem para escolher. Com a captura liberada, os do plano
 * (`search.types`). Sem ela (licença vencida), os de antes continuam à vista e levam à oferta.
 */
export function captureTypes(session: EntitlementSession, now = Date.now()): string[] {
  if (can(session, 'capture.enabled', now)) return itemsOf(session, 'search.types', now);
  const modules = session?.modules ?? {};
  return SEARCH_TYPES.filter((type) => Boolean(modules[type]));
}

/** Conta em demonstração (ativa ou encerrada). */
export function isTrial(
  session: { demo?: boolean; entitlements?: Pick<Entitlements, 'state'> } | null | undefined,
): boolean {
  const state = session?.entitlements?.state;
  if (state) return state === 'trial' || state === 'trial_ended';
  return session?.demo === true;
}
